import { useRef, useState } from "react";
import emailjs from "emailjs-com";
import { AnimatePresence, motion } from "framer-motion";
import { FiMail, FiSend, FiMessageSquare, FiLinkedin, FiGithub } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import SectionHeading from "../../components/SectionHeading";
import Reveal from "../../components/Reveal";

const channels = [
  {
    icon: FiMail,
    label: "Email",
    value: "shahedtalukder51@gmail.com",
    href: "mailto:shahedtalukder51@gmail.com",
    color: "text-content",
  },
  {
    icon: FiMessageSquare,
    label: "Messenger",
    value: "m.me/shahed.talukder32",
    href: "https://m.me/shahed.talukder32",
    color: "text-content",
  },
  {
    icon: FaWhatsapp,
    label: "WhatsApp",
    value: "+880 1762 178238",
    href: "https://api.whatsapp.com/send/?phone=01762178238",
    color: "text-emerald-400",
  },
];

const Contact = () => {
  const form = useRef();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus("sending");
    emailjs
      .sendForm(
        "service_ks0njt1",
        "template_ckb2gor",
        form.current,
        "BK3SA6zornN0cTg4Y"
      )
      .then(
        () => {
          setStatus("sent");
          form.current.reset();
          setTimeout(() => setStatus("idle"), 4000);
        },
        () => {
          setStatus("error");
          setTimeout(() => setStatus("idle"), 4000);
        }
      );
  };

  return (
    <section id="contact" className="relative px-6 py-28 md:py-36">
      <div className="container mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Contact"
          title="Let's Create Together"
          subtitle="Open to new opportunities, collaborations, and interesting problems. Drop a message and let's make something amazing."
        />

        <div className="grid gap-6 lg:grid-cols-5">
          {/* info */}
          <Reveal direction="right" className="lg:col-span-2">
            <div className="glass flex h-full flex-col rounded-3xl p-8">
              <h3 className="text-2xl font-semibold text-content">Get in touch</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                Whether you have a project in mind or just want to say hello, my
                inbox is always open.
              </p>

              <div className="mt-8 flex flex-col gap-4">
                {channels.map(({ icon: Icon, label, value, href, color }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center gap-4 rounded-2xl border border-hairline bg-fill p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-hairline hover:bg-fill-2"
                  >
                    <span
                      className={`flex h-11 w-11 items-center justify-center rounded-xl border border-hairline bg-fill text-lg ${color}`}
                    >
                      <Icon />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-xs uppercase tracking-wider text-faint">
                        {label}
                      </span>
                      <span className="text-sm font-medium text-content">
                        {value}
                      </span>
                    </span>
                  </a>
                ))}
              </div>

              <div className="mt-auto flex gap-3 pt-8">
                <a
                  href="https://www.linkedin.com/in/shahed-talukder/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-hairline bg-fill text-muted transition-all hover:-translate-y-1 hover:text-content hover:shadow-glow"
                >
                  <FiLinkedin />
                </a>
                <a
                  href="https://github.com/shahed-swe"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-hairline bg-fill text-muted transition-all hover:-translate-y-1 hover:text-content hover:shadow-glow"
                >
                  <FiGithub />
                </a>
              </div>
            </div>
          </Reveal>

          {/* form */}
          <Reveal direction="left" delay={0.1} className="lg:col-span-3">
            <form
              ref={form}
              onSubmit={sendEmail}
              className="glass flex flex-col gap-5 rounded-3xl p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field name="name" type="text" label="Your name" placeholder="Jane Doe" />
                <Field name="email" type="email" label="Your email" placeholder="jane@email.com" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-wider text-faint">
                  Message
                </label>
                <textarea
                  name="message"
                  rows="6"
                  required
                  placeholder="Tell me about your project..."
                  className="resize-none rounded-2xl border border-hairline bg-fill px-4 py-3 text-content placeholder-slate-500 outline-none transition-all duration-300 focus:border-content focus:bg-fill-2 focus:shadow-glow"
                />
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <motion.button
                  type="submit"
                  disabled={status === "sending"}
                  whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center gap-2 rounded-full bg-content px-8 py-3.5 font-semibold text-surface shadow-glow transition-all hover:opacity-90 disabled:opacity-60"
                >
                  {status === "sending" ? "Sending..." : "Send Message"}
                  <FiSend
                    className={status === "sending" ? "animate-pulse" : ""}
                  />
                </motion.button>

                <AnimatePresence>
                  {status === "sent" && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm font-medium text-emerald-400"
                    >
                      ✓ Message sent — thank you!
                    </motion.span>
                  )}
                  {status === "error" && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm font-medium text-rose-400"
                    >
                      Something went wrong — please try again.
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

function Field({ name, type, label, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs uppercase tracking-wider text-faint">
        {label}
      </label>
      <input
        type={type}
        name={name}
        required
        placeholder={placeholder}
        className="rounded-2xl border border-hairline bg-fill px-4 py-3 text-content placeholder-slate-500 outline-none transition-all duration-300 focus:border-content focus:bg-fill-2 focus:shadow-glow"
      />
    </div>
  );
}

export default Contact;
